import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Input } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import {
  updatePasswordBizAccountAction,
  updatePasswordPINAction,
} from '../../store/login/actions';
import {
  getCurrentPassword,
  getCustomerCode,
  getIsNewAccount,
  getLoginStatus,
  getPhoneNumber,
  getToken,
  getUserProfile,
} from '../../store/login/selectors';
import {useTranslation} from "react-i18next";

const CreateNewPassword = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const token = useSelector(getToken);
  const loginStatus = useSelector(getLoginStatus);
  const phoneNumber = useSelector(getPhoneNumber);
  const customerCode = useSelector(getCustomerCode);
  const userProfile = useSelector(getUserProfile);
  const currentPassword = useSelector(getCurrentPassword);
  const isNewAccount = useSelector(getIsNewAccount);
  const navigate = useNavigate();
  const {t} = useTranslation();
  const [errorPassword, setErrorPassword] = useState('');
  const [errorConfirmationPassword, setErrorConfirmationPassword] =
    useState('');

  useEffect(() => {
    if (!phoneNumber && !customerCode) {
      navigate('/');
    }
    if (loginStatus) {
      if (!userProfile || (userProfile && !userProfile.loginFirst)) {
        navigate('/');
      }
    }
  }, [phoneNumber, customerCode, loginStatus, userProfile]);

  const validateForm = () => {
    const { password, password_confirmation } = form.getFieldsValue();
    let isValid = true;
    setErrorPassword('');
    setErrorConfirmationPassword('');

    if (!password || !password.length) {
      setErrorPassword('validation_required_password');
      isValid = false;
    } else if (password.slice(0, 1) === ' ' || password.slice(-1) === ' ') {
      setErrorPassword(
        'validation_spaces_password',
      );
      isValid = false;
    } else if (password.length < 6 || password.length > 20) {
      setErrorPassword('validation_range_length_password');
      isValid = false;
    }

    if (!password_confirmation || !password_confirmation.length) {
      setErrorConfirmationPassword('validation_required_password');
      isValid = false;
    } else if (password && password !== password_confirmation) {
      setErrorConfirmationPassword('validation_required_re_enter_password');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const { password } = form.getFieldsValue();
      if (customerCode && !isNewAccount) {
        //  only call this action for first login
        dispatch(
          updatePasswordBizAccountAction({
            old_password: currentPassword,
            new_password: password,
          }),
        );
      } else {
		// 2 case: create password for biz account & cus acc
        dispatch(
          updatePasswordPINAction({
            phone: customerCode || phoneNumber,
            pin: password,
            verified_token: token,
          }),
        );
      }
    }
  };

  return (
    <>
      <Form
        layout="vertical"
        className="w-100"
        form={form}
        onValuesChange={() => validateForm()}
        onFinish={handleSubmit}
      >
        <Form.Item>
          <div className="page-title mb-2 header-title">{t('section_title_create_new_password')}</div>
          <p className="mb-2">{t('section_sub_title_create_new_password')}</p>
        </Form.Item>
        <Form.Item label={customerCode ? t('global_customer_code') : t('global_phone')}>
          <Input disabled value={customerCode || phoneNumber} />
        </Form.Item>
        <Form.Item label={t('global_password')} help={t(errorPassword, {min: 6, max: 20})} name="password">
          <Input.Password
            placeholder={t('placeholder_password')}
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
            autoFocus={true}
          />
        </Form.Item>
        <Form.Item
          label={t('input_label_re_enter_password')}
          help={t(errorConfirmationPassword)}
          name="password_confirmation"
        >
          <Input.Password
            placeholder={t('placeholder_re_enter_password')}
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            {t('button_complete')}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default CreateNewPassword;
